import { Views } from 'react-big-calendar';

const WEEKDAY_LABELS = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
const MONTH_ROWS = 5;
const TIME_SLOTS = 9;

const SkeletonBar = ({ className = '' }) => (
    <span className={`cal-skeleton__bar ${className}`.trim()} aria-hidden="true" />
);

const SkeletonToolbar = () => (
    <div className="cal-skeleton__toolbar" aria-hidden="true">
        <div className="cal-skeleton__toolbar-nav">
            <SkeletonBar className="cal-skeleton__bar--icon" />
            <SkeletonBar className="cal-skeleton__bar--title" />
            <SkeletonBar className="cal-skeleton__bar--icon" />
        </div>
        <div className="cal-skeleton__toolbar-actions">
            <SkeletonBar className="cal-skeleton__bar--today" />
            <SkeletonBar className="cal-skeleton__bar--views" />
        </div>
    </div>
);

const MonthGridSkeleton = () => (
    <div className="cal-skeleton__month">
        <div className="cal-skeleton__weekdays">
            {WEEKDAY_LABELS.map((label) => (
                <span key={label} className="cal-skeleton__weekday">
                    {label}
                </span>
            ))}
        </div>
        {Array.from({ length: MONTH_ROWS }, (_, row) => (
            <div key={row} className="cal-skeleton__month-row">
                {WEEKDAY_LABELS.map((label, col) => (
                    <div
                        key={`${row}-${label}`}
                        className={`cal-skeleton__month-cell cal-skeleton__month-cell--${(row + col) % 4}`}
                    >
                        <SkeletonBar className="cal-skeleton__bar--date" />
                        {(row + col) % 3 !== 0 ? (
                            <div className="cal-skeleton__month-pills">
                                <SkeletonBar className="cal-skeleton__bar--pill" />
                                {(row + col) % 2 === 0 ? (
                                    <SkeletonBar className="cal-skeleton__bar--pill cal-skeleton__bar--pill-short" />
                                ) : null}
                            </div>
                        ) : null}
                    </div>
                ))}
            </div>
        ))}
    </div>
);

const TimeGridSkeleton = ({ dayCount }) => (
    <div
        className="cal-skeleton__time"
        style={{ '--cal-skeleton-days': dayCount }}
    >
        <div className="cal-skeleton__time-head">
            <span className="cal-skeleton__gutter-head" aria-hidden="true" />
            {Array.from({ length: dayCount }, (_, index) => (
                <div key={index} className="cal-skeleton__day-head">
                    <SkeletonBar className="cal-skeleton__bar--dow" />
                    <SkeletonBar className="cal-skeleton__bar--dom" />
                </div>
            ))}
        </div>
        <div className="cal-skeleton__time-body">
            <div className="cal-skeleton__gutter">
                {Array.from({ length: TIME_SLOTS }, (_, index) => (
                    <SkeletonBar key={index} className="cal-skeleton__bar--hour" />
                ))}
            </div>
            <div className="cal-skeleton__day-columns">
                {Array.from({ length: dayCount }, (_, col) => (
                    <div key={col} className="cal-skeleton__day-column">
                        {Array.from({ length: TIME_SLOTS }, (_, row) => (
                            <div key={row} className="cal-skeleton__slot" />
                        ))}
                        {(col % 3 !== 2) ? (
                            <SkeletonBar
                                className={`cal-skeleton__bar--event cal-skeleton__bar--event-${(col % 3) + 1}`}
                            />
                        ) : null}
                    </div>
                ))}
            </div>
        </div>
    </div>
);

/**
 * Esqueleto del calendario según la vista activa (mes, semana o día).
 */
export const CalendarSkeleton = ({ view = Views.WEEK, overlay = false }) => {
    const isMonth = view === Views.MONTH;
    const isDay = view === Views.DAY;
    const dayCount = isDay ? 1 : 7;

    return (
        <div
            className={`cal-skeleton${overlay ? ' cal-skeleton--overlay' : ''}`}
            role="status"
            aria-live="polite"
            aria-busy="true"
            aria-label="Cargando calendario"
        >
            <SkeletonToolbar />
            {isMonth ? <MonthGridSkeleton /> : <TimeGridSkeleton dayCount={dayCount} />}
            <span className="sr-only">Cargando eventos del calendario…</span>
        </div>
    );
};
